import React from 'react';
import styled, { css } from 'styled-components';
import { Icon, IconTypes, Tooltip } from 'components';
import { theme, toRem } from 'design';
import Utils from 'lib/Utils';

interface IIconOption {
  icon: string;
  value: string;
  size?: number;
  tooltip?: string | React.ReactNode;
}

interface InputIconsProps {
  value?: string;
  options: IIconOption[];
  id?: string;
  disabled?: boolean;
  error?: boolean;
  onIconClick?: (value: string) => void;
  width?: number;
}

const Layout = styled.div<Partial<InputIconsProps>>`
  display: flex;
  align-items: center;
  justify-content: center;
  height: ${toRem(44)};
  padding: ${toRem(theme.space.sm)} ${toRem(theme.space.sm)};
  gap: ${toRem(theme.space.sm)};
  border: ${toRem(1)} solid ${theme.color.neutral[200]};
  border-radius: ${toRem(5.5)};
  background-color: ${theme.color.neutral[100]};

  ${({ width }) =>
    width &&
    css`
      width: ${toRem(width)};
    `}

  ${({ disabled }) =>
    !disabled &&
    css`
      background-color: ${theme.color.white};

      &:hover,
      &:focus-visible {
        border-color: ${theme.color.neutral[400]};
      }
    `}

  ${({ error }) =>
    error &&
    css`
      border-color: ${theme.color.danger[50]};

      &:hover,
      &:focus-visible {
        border-color: ${theme.color.danger[50]};
      }
    `}
`;

interface IconContainerProps extends InputIconsProps {
  active?: boolean;
}

const IconContainer = styled.button.attrs({ type: 'button' })<Partial<IconContainerProps>>`
  position: relative;
  width: ${({ width }) => toRem(width || 16)};
  height: ${toRem(16)};

  svg {
    fill: ${({ active }) => (active ? theme.color.secondary[400] : theme.color.neutral[300])};
    transition: fill ${theme.transition.icon};
  }

  ${({ disabled, active }) =>
    disabled
      ? css`
          cursor: auto;
        `
      : css`
          &:hover,
          &:focus-visible {
            svg {
              fill: ${active ? theme.color.secondary[400] : theme.color.neutral[500]};
            }
          }
        `}
`;

const InputIcons = ({ value, options, id = 'icon', disabled, error, onIconClick, width, ...rest }: InputIconsProps) => (
  <Layout disabled={disabled} error={error} width={width} {...rest}>
    {options.map((option) => (
      <React.Fragment key={`icon_${option.value}`}>
        <IconContainer aria-label="Click to select" active={option.value === value} onClick={() => onIconClick(option.value)} disabled={disabled} width={option.size} data-tooltip-id={`${id}_${option.value}`}>
          <Icon icon={option.icon as IconTypes} width={option.size || 16} height={option.size || 16} />
        </IconContainer>

        {option.tooltip && !Utils.isMobileTouchDevice() && <Tooltip id={`${id}_${option.value}`}>{option.tooltip}</Tooltip>}
      </React.Fragment>
    ))}
  </Layout>
);

export default InputIcons;
